/**
 * Текст-нарратив «как отвечает ИИ» из структурного результата анализа.
 * Чистая функция — никакой модели не требуется: формирует читаемую сводку
 * по тем же данным, что показывают вкладки отчёта.
 */
import { AllMethodologiesResult, BrokerCalcResult } from "../types";
import { formatRubles, parseRiskScore } from "./math_helpers";
import { FLAG_LABEL, SORT_LABEL } from "./agentEngine";

const BROKER_CAT: Record<string, string> = {
  gos: "госзакупки (44/223)", comm: "коммерческая", tax: "налоговая", customs: "таможенная",
};

const BROKER_GT: Record<string, string> = {
  bid: "обеспечение заявки", exec_no_adv: "исполнение (без аванса)",
  exec_adv: "исполнение (с авансом)", warranty: "гарантийные обязательства",
  adv_return: "возврат аванса",
};
const BROKER_LAW: Record<string, string> = {
  "44": "44-ФЗ", "223": "223-ФЗ", "615": "615-ПП", "275": "275-ФЗ (ГОЗ)", "185": "185-ФЗ",
};

// Текст-сводка по подбору банковских гарантий (режим «Брокер»). `r.offers` — это уже
// «вид» результата (отфильтрованный/отсортированный/усечённый в компоненте), а `r.count`
// и `r.eligibleCount` — итоги по всему подбору.
export function narrateBroker(r: BrokerCalcResult, p: {
  amount: number; days: number; category: string;
  guaranteeType?: string | null; law?: string | null;
  flags?: Record<string, boolean>; sort?: string | null; limit?: number | null;
}): string {
  const conds = Object.keys(p.flags || {}).filter((k) => p.flags![k]).map((k) => FLAG_LABEL[k] || k);
  const vid = p.guaranteeType ? `${BROKER_GT[p.guaranteeType] || p.guaranteeType}, ` : "";
  const law = p.law ? `${BROKER_LAW[p.law] || p.law}, ` : "";
  const head = `Подбор гарантии: ${vid}${law}сумма ${formatRubles(p.amount)}, срок ${p.days} дн., ` +
    `закупка — ${BROKER_CAT[p.category] || p.category}${conds.length ? `; условия: ${conds.join(", ")}` : ""}.`;
  const offers = r.offers || [];
  if (!offers.length) {
    if (!r.count) return head + "\n\nВ реестре нет банков, работающих с этой категорией.";
    return head + `\n\nПод заданные фильтры результата ничего не попало (всего в категории: ${r.count}). ` +
      "Ослабьте фильтр — уберите «только проходящие» или источник.";
  }
  const cnt = `Банков в категории: ${r.count}, проходят по условиям: ${r.eligibleCount}.`;
  const viewNote = (p.sort || p.limit)
    ? `Показаны: ${SORT_LABEL[p.sort || "best"] || "рекомендуемые"}${p.limit ? ` · топ-${p.limit}` : ""}.`
    : "";

  const eligible = offers.filter((o) => o.eligible);
  if (!eligible.length) {
    const why = offers.slice(0, 3).map((o) => `• ${o.bank}: ${o.reasons[0] || "не проходит"}`).join("\n");
    const lines = [head, cnt];
    if (viewNote) lines.push(viewNote);
    lines.push("", "Подходящих под параметры нет. Причины:", why, "", "Детали по каждому банку — ниже.");
    return lines.join("\n");
  }

  const lead = offers[0];   // первый по выбранному порядку (напр. «самый дорогой»/«рекомендуемый»)
  const leadLabel = p.sort === "expensive" ? "Самый дорогой" : p.sort === "kv" ? "Максимальный КВ агента"
    : p.sort === "cheap" ? "Самый выгодный" : "Рекомендуем";
  const topN = Math.min(p.limit || 5, offers.length);
  const top = offers.slice(0, topN).map((o) =>
    `• ${o.bank}${o.rating ? ` (${o.rating})` : ""}${o.eligible ? "" : " — не проходит"}: комиссия ${formatRubles(o.commission)}, ` +
    `КВ ${formatRubles(o.av)}${o.avRate ? ` (${Math.round(o.avRate * 100)}%)` : ""}`).join("\n");
  const lines = [head, cnt];
  if (viewNote) lines.push(viewNote);
  lines.push("",
    `${leadLabel} — ${lead.bank}: комиссия ${formatRubles(lead.commission)}${lead.avRate ? `, КВ ${Math.round(lead.avRate * 100)}%` : ""}.`,
    "", "Список:", top,
    "", "Полные карточки с лимитами, документами и кнопкой «Подать заявку» — ниже.");
  return lines.join("\n");
}

// Короткий вердикт по буквенному рейтингу ANYskin (AAA…D).
const VERDICT: Record<string, string> = {
  AAA: "очень высокая надёжность — лимит можно рассматривать на стандартных условиях",
  AA: "высокая надёжность — условия близки к стандартным",
  A: "хорошая надёжность при умеренном контроле",
  BBB: "умеренная устойчивость — рекомендуется контроль лимита и мониторинг",
  BB: "повышенный риск — целесообразны обеспечение и лимиты отгрузки",
  B: "высокий риск — работа с обеспечением или предоплатой",
  C: "критический риск — рекомендуется предоплата",
  D: "крайне высокий риск — без полной предоплаты сотрудничество не рекомендуется",
};

export function narrate(r: AllMethodologiesResult, amount: number): string {
  const c = r.company;
  const sb = parseRiskScore(r.company.riskCheckScore);
  const stops = (r.anyskin?.details?.stopFactors || [])
    .filter((s) => s.active)
    .map((s) => s.label);
  const verdict = VERDICT[r.anyskin?.rating] || "оценка сформирована";

  const head = `${c.fullName || "Контрагент"} (ИНН ${c.inn})${c.status ? `, статус: ${c.status}` : ""}.`;

  const ratings = [
    `• Рейтинг ANYskin — ${r.anyskin.rating} (${r.anyskin.totalScore} из 20 баллов): ${verdict}.`,
    `• Кредитоспособность — риск «${r.credit.riskCategory}» (F-score ${r.credit.fScore}).`,
    `• Платёжеспособность — ${r.solvency.interpretation}.`,
    `• 100-балльная экспресс-оценка СЭБ Anyskin — ${r.score.totalPoints}/100, ${r.score.scoreClass}; группа TOP банка — ${r.score.sberbankClass}.`,
    `• Индекс благонадёжности СЭБ Anyskin — ${sb.short}${sb.label ? ` (${sb.label})` : ""}.`,
    r.krylov?.calculated
      ? `• Углублённый анализ отчётности — ${Object.values(r.krylov.methodologies || {}).map((m) => m.rating).join("; ")}.`
      : "",
  ].filter(Boolean).join("\n");

  const portfolio =
    `Госконтракты: ${r.contracts.totalCount} на ${formatRubles(r.contracts.totalSum)} ` +
    `(завершено ${r.contracts.completedCount}). Судебные споры как ответчик: ` +
    `${r.legalCases.defendantCount} на ${formatRubles(r.legalCases.defendantSum)}.`;

  const stopsLine = stops.length ? `Стоп-факторы: ${stops.join("; ")}.` : "Стоп-факторы не выявлены.";
  const limitLine = `Запрошенный лимит для оценки: ${formatRubles(amount)}.`;
  const src = r.reportBased
    ? "Источник данных: загруженная вами отчётность (суммы стоит сверить)."
    : "Источник данных: открытые федеральные ресурсы.";

  const recs = (r.recommendations || []).slice(0, 4);
  const recBlock = recs.length
    ? "Что улучшить:\n" + recs.map((x) => `• ${x.action} — ${x.detail}`).join("\n")
    : "Критичных замечаний нет — ключевые показатели в норме.";
  const target = r.targetReport
    ? `Целевая отчётность: ${r.targetReport.summary}`
    : "";

  return [head, "", ratings, "", portfolio, stopsLine, "", recBlock,
    ...(target ? ["", target] : []), "", limitLine, src,
    "", "Полную детализацию, сводный план и целевой баланс развернул в блоках ниже."].join("\n");
}
